import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import {
  GraduationCap, Target, TrendingUp, Clock, DollarSign, Briefcase,
  ChevronRight, Zap, Award, BookOpen, Plus, Play, Star,
  ArrowRight, CheckCircle2, Sparkles, BarChart3, User, Shield,
  Gauge, Activity, Info, X
} from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stagger = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const fadeUp = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } } };

const PATH_ICONS = {
  on_time: GraduationCap, summer_courses: BookOpen, add_minor: BookOpen,
  intern_cert: Briefcase, masters: GraduationCap, shift_major: Target,
};

const fmt = (v) => `$${Math.round((v || 0) / 1000)}K`;

export default function PathSimulatorPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [combined, setCombined] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [activePath, setActivePath] = useState('on_time');
  const [simDone, setSimDone] = useState(false);

  const [levers, setLevers] = useState({
    salary_upside: 70,
    market_comp: 60,
    risk_tolerance: 50,
    ai_disruption: 70,
  });

  useEffect(() => {
    if (!token) return;
    axios.post(`${API}/college/strategy-simulate`, { selected_paths: [] }, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const simulate = useCallback(async () => {
    if (selected.length < 2) return;
    setSimulating(true);
    setSimDone(false);
    try {
      const r = await axios.post(`${API}/college/strategy-simulate`, { selected_paths: selected }, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
      setCombined(r.data.combined);
      setSimDone(true);
    } catch {}
    setSimulating(false);
  }, [selected, token]);

  const togglePath = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
    setCombined(null);
    setSimDone(false);
  };

  const updateLever = (key, value) => setLevers(prev => ({ ...prev, [key]: value }));

  const pathList = useMemo(() => {
    if (!data?.paths) return [];
    const raw = Object.values(data.paths);
    const salaryMult = 0.85 + (levers.salary_upside / 100) * 0.3;
    const riskMult = 0.9 + (levers.risk_tolerance / 100) * 0.2;
    return raw.map(p => ({
      ...p,
      projections: p.projections.map(v => Math.round(v * salaryMult * riskMult)),
      ten_yr_earnings: Math.round(p.ten_yr_earnings * salaryMult * riskMult),
      job_landing: Math.min(99, Math.round(p.job_landing * (0.9 + (levers.market_comp / 100) * 0.2))),
    }));
  }, [data, levers]);

  // Apply lever multipliers to combined too
  const scaledCombined = useMemo(() => {
    if (!combined) return null;
    const salaryMult = 0.85 + (levers.salary_upside / 100) * 0.3;
    const riskMult = 0.9 + (levers.risk_tolerance / 100) * 0.2;
    return {
      ...combined,
      projections: combined.projections.map(v => Math.round(v * salaryMult * riskMult)),
      ten_yr_earnings: Math.round(combined.ten_yr_earnings * salaryMult * riskMult),
      job_landing: Math.min(99, Math.round(combined.job_landing * (0.9 + (levers.market_comp / 100) * 0.2))),
    };
  }, [combined, levers]);

  if (loading) return <AppLayout><div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner size="lg" /></div></AppLayout>;
  if (!data) return <AppLayout><div className="text-center py-20 text-slate-300">Unable to load simulation engine.</div></AppLayout>;

  const { student, paths, grad_school_roi, best_move, timeline } = data;

  return (
    <AppLayout>
      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-5 max-w-[1400px] mx-auto" data-testid="strategy-simulation">

        {/* HERO */}
        <motion.div variants={fadeUp}>
          <div className="relative overflow-hidden rounded-[18px] p-7 md:p-9"
            style={{
              background: 'rgba(6, 23, 54, 0.72)', backdropFilter: 'blur(16px)',
              border: '1px solid rgba(57, 227, 220, 0.28)',
              boxShadow: '0 0 8px rgba(57,227,220,0.65), 0 0 20px rgba(57,227,220,0.30), 0 0 42px rgba(57,227,220,0.12)',
            }}>
            <div aria-hidden className="pointer-events-none absolute -top-28 -right-20 w-[26rem] h-[26rem] rounded-full blur-3xl opacity-25"
              style={{ background: 'radial-gradient(circle, #39E3DC, transparent 65%)' }} />
            <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-16 w-[20rem] h-[20rem] rounded-full blur-3xl opacity-15"
              style={{ background: 'radial-gradient(circle, #782FE1, transparent 65%)' }} />
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(57,227,220,0.55), transparent)' }} />
            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-[#39E3DC] animate-pulse" style={{ boxShadow: '0 0 8px rgba(57,227,220,0.6)' }} />
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#39E3DC] font-bold">Strategy Engine</p>
                </div>
                <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white tracking-tight"
                  style={{ textShadow: '0 0 60px rgba(57,227,220,0.2)' }}>
                  Strategy Simulator
                </h1>
                <p className="text-base text-[#C6D4E8] mt-2 max-w-xl">
                  What happens if you add a minor? Stay for a master's? This page lets you <span className="text-[#39E3DC] font-semibold">compare different strategies side-by-side</span> and see which one gives you the best outcome in salary, debt, and job landing rate.
                </p>
              </div>
              <div className="flex items-center gap-3 rounded-2xl border border-[rgba(57,227,220,0.22)] bg-[#0B1F46] px-5 py-3"
                style={{ boxShadow: 'inset 0 0 12px rgba(57,227,220,0.04)' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white"
                  style={{ background: 'linear-gradient(90deg, #352CA0, #782FE1, #2E86E2)', boxShadow: '0 0 20px rgba(120,47,225,0.4)' }}>
                  {student.name?.charAt(0)}
                </div>
                <div>
                  <p className="text-white font-outfit font-bold">{student.name}</p>
                  <p className="text-slate-300 text-xs">{student.major} | {student.career_target}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* HOW IT WORKS */}
        <motion.div variants={fadeUp}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { step: '1', icon: Target, title: 'Pick 2+ Paths', desc: 'Select strategies you want to compare (e.g. "Graduate On Time" vs "Add a Minor")' },
              { step: '2', icon: Play, title: 'Hit Simulate', desc: 'We\'ll combine them and show you the projected outcome over 10 years' },
              { step: '3', icon: Gauge, title: 'Adjust Levers', desc: 'Change market assumptions to see how outcomes shift under different conditions' },
            ].map((item, i) => (
              <div key={i} className="rounded-xl border border-[rgba(89,126,190,0.22)] bg-[#061736] p-4 flex items-start gap-3"
                style={{ boxShadow: 'inset 0 0 10px rgba(57,227,220,0.02)' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, rgba(57,227,220,0.15), rgba(120,47,225,0.15))', border: '1px solid rgba(57,227,220,0.25)' }}>
                  {item.step}
                </div>
                <div>
                  <p className="text-sm text-white font-semibold">{item.title}</p>
                  <p className="text-[11px] text-[#C6D4E8] mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* PICK PATHS (horizontal strip) + SIMULATE */}
        <motion.div variants={fadeUp}>
          <div className="rounded-2xl border border-[rgba(57,227,220,0.22)] bg-[#061736] p-4"
            style={{ boxShadow: '0 0 6px rgba(57,227,220,0.35), 0 0 14px rgba(57,227,220,0.12)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-white font-outfit font-bold text-base">Pick Your Paths</h2>
                <span className="text-[10px] text-[#C6D4E8] bg-[#0B1F46] px-2 py-0.5 rounded-full">{selected.length} selected</span>
              </div>
              <p className="text-[11px] text-[#C6D4E8]">Select 2+, then simulate to compare outcomes</p>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {pathList.map((path) => {
                const isSelected = selected.includes(path.id);
                return (
                  <button key={path.id} onClick={() => togglePath(path.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                      isSelected ? 'text-white' : 'text-[#C6D4E8] hover:text-white'
                    }`}
                    style={{
                      border: `1px solid ${isSelected ? path.color + '60' : 'rgba(89,126,190,0.22)'}`,
                      background: isSelected ? path.color + '15' : 'rgba(11,31,70,0.4)',
                      boxShadow: isSelected ? `0 0 10px ${path.color}33` : 'none',
                    }}
                    data-testid={`select-${path.id}`}>
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: path.color, boxShadow: `0 0 6px ${path.color}55` }} />
                    {path.label}
                    <span className="text-[10px] text-[#C6D4E8]">{fmt(path.projections[10])}</span>
                    {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-[#39E3DC]" />}
                  </button>
                );
              })}
            </div>
            {/* Simulate CTA */}
            {selected.length >= 2 ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-[#39E3DC] uppercase tracking-wider font-bold">Combining</span>
                  {selected.map((id, i) => (
                    <span key={id}>
                      <span className="text-[10px] px-2 py-0.5 rounded-full border text-white" style={{ borderColor: paths[id]?.color + '60', backgroundColor: paths[id]?.color + '15' }}>
                        {paths[id]?.label}
                      </span>
                      {i < selected.length - 1 && <span className="mx-0.5 text-[10px] text-[#39E3DC]">+</span>}
                    </span>
                  ))}
                </div>
                <button onClick={simulate} disabled={simulating}
                  className="fp-btn-primary flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm disabled:opacity-50"
                  data-testid="simulate-btn">
                  <Play className="w-4 h-4" /> {simulating ? 'Simulating...' : 'SIMULATE'}
                </button>
              </div>
            ) : (
              <p className="text-[11px] text-[#C6D4E8]">Select at least 2 paths above, then hit Simulate</p>
            )}
          </div>
        </motion.div>

        {/* COMPARE OUTCOMES, full width chart */}
        <motion.div variants={fadeUp}>
          <div className="rounded-2xl border border-[rgba(57,227,220,0.22)] bg-[#061736] p-5"
            style={{ boxShadow: '0 0 6px rgba(57,227,220,0.35), 0 0 14px rgba(57,227,220,0.12)' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-white font-outfit font-bold text-lg">Compare Outcomes</h2>
                  <p className="text-[11px] text-[#C6D4E8]">Projected salary over 10 years for each path</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {pathList.map(p => (
                    <span key={p.id} className="flex items-center gap-1 text-[10px] text-[#C6D4E8]">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color, boxShadow: `0 0 4px ${p.color}66` }} />{p.label.split(' ')[0]}
                    </span>
                  ))}
                  {scaledCombined && (
                    <span className="flex items-center gap-1 text-[10px] text-[#39E3DC] font-bold">
                      <span className="w-2 h-2 rounded-full bg-[#39E3DC]" style={{ boxShadow: '0 0 6px rgba(57,227,220,0.6)' }} />Combined
                    </span>
                  )}
                </div>
              </div>

              {/* CHART */}
              <div className="rounded-xl border border-[rgba(89,126,190,0.15)] bg-[#0B1F46] p-4">
                <SalaryChart paths={pathList} combined={scaledCombined} activePath={activePath} />
              </div>

              {/* Outcome table */}
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-[#C6D4E8] uppercase tracking-wider">
                      <th className="text-left pb-2 font-bold">Metric</th>
                      {pathList.map(p => (
                        <th key={p.id} className="text-center pb-2 font-bold" style={{ color: p.color }}>{p.label.split(' ').slice(0, 2).join(' ')}</th>
                      ))}
                      {scaledCombined && <th className="text-center pb-2 font-bold text-[#39E3DC]">Combined</th>}
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    <tr className="border-t border-[rgba(89,126,190,0.12)]">
                      <td className="py-2 text-[#C6D4E8] font-semibold">10-YR EARNINGS</td>
                      {pathList.map(p => <td key={p.id} className="text-center font-bold text-white">{fmt(p.ten_yr_earnings)}</td>)}
                      {scaledCombined && <td className="text-center font-bold text-[#39E3DC]" style={{ textShadow: '0 0 8px rgba(57,227,220,0.3)' }}>{fmt(combined.ten_yr_earnings)}</td>}
                    </tr>
                    <tr className="border-t border-[rgba(89,126,190,0.12)]">
                      <td className="py-2 text-[#C6D4E8] font-semibold">TOTAL COST</td>
                      {pathList.map(p => <td key={p.id} className="text-center">{fmt(p.total_cost)}</td>)}
                      {scaledCombined && <td className="text-center text-[#ECC451]">{fmt(combined.total_cost)}</td>}
                    </tr>
                    <tr className="border-t border-[rgba(89,126,190,0.12)]">
                      <td className="py-2 text-[#C6D4E8] font-semibold">DEBT AT GRAD</td>
                      {pathList.map(p => <td key={p.id} className="text-center">{fmt(p.debt_at_grad)}</td>)}
                      {scaledCombined && <td className="text-center text-[#ECC451]">{fmt(combined.debt_at_grad)}</td>}
                    </tr>
                    <tr className="border-t border-[rgba(89,126,190,0.12)]">
                      <td className="py-2 text-[#C6D4E8] font-semibold">JOB LANDING %</td>
                      {pathList.map(p => (
                        <td key={p.id} className={`text-center font-semibold ${p.job_landing >= 80 ? 'text-[#15BA89]' : p.job_landing >= 70 ? 'text-[#ECC451]' : 'text-slate-300'}`}>
                          {p.job_landing}%
                        </td>
                      ))}
                      {scaledCombined && <td className="text-center font-bold text-[#15BA89]">{combined.job_landing}%</td>}
                    </tr>
                    <tr className="border-t border-[rgba(89,126,190,0.12)]">
                      <td className="py-2 text-[#C6D4E8] font-semibold">SATISFACTION</td>
                      {pathList.map(p => <td key={p.id} className="text-center">{p.satisfaction}/10</td>)}
                      {scaledCombined && <td className="text-center text-[#39E3DC]">{combined.satisfaction}/10</td>}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Simulation result banner */}
              <AnimatePresence>
                {simDone && scaledCombined && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-4">
                    <div className="rounded-xl p-4" style={{
                      background: 'linear-gradient(135deg, rgba(57,227,220,0.08), rgba(120,47,225,0.08))',
                      border: '1px solid rgba(57,227,220,0.35)',
                      boxShadow: '0 0 6px rgba(57,227,220,0.55), 0 0 18px rgba(57,227,220,0.25), inset 0 0 18px rgba(57,227,220,0.03)',
                    }}>
                      <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-[#39E3DC] flex-shrink-0" style={{ filter: 'drop-shadow(0 0 6px rgba(57,227,220,0.6))' }} />
                        <div>
                          <p className="text-white font-outfit font-bold text-sm">Simulation: {scaledCombined.label}</p>
                          <p className="text-[#C6D4E8] text-xs mt-1">
                            Combined strategy projects <span className="text-[#39E3DC] font-bold">{fmt(scaledCombined.ten_yr_earnings)}</span> over 10 years
                            with {fmt(scaledCombined.total_cost)} additional cost and {scaledCombined.job_landing}% job landing rate.
                            {scaledCombined.extra_time > 0 && ` Adds ~${scaledCombined.extra_time} months to your timeline.`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
        </motion.div>

        {/* ADJUST LEVERS */}
        <motion.div variants={fadeUp}>
          <div className="relative overflow-hidden rounded-2xl border border-[rgba(57,227,220,0.22)] bg-[#061736] p-5"
            style={{ boxShadow: '0 0 6px rgba(57,227,220,0.35), 0 0 14px rgba(57,227,220,0.12)' }} data-testid="adjust-levers">
            <div className="relative">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg border border-[rgba(57,227,220,0.28)] bg-[rgba(57,227,220,0.06)] flex items-center justify-center">
                  <Gauge className="w-4 h-4 text-[#39E3DC]" />
                </div>
                <h2 className="text-white font-outfit font-bold text-lg">Adjust Assumptions</h2>
              </div>
              <p className="text-[11px] text-[#C6D4E8] mb-4 ml-10">Change these market assumptions and watch how the numbers in the table above shift in real time.</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4">
                <Lever label="Salary Outlook" value={levers.salary_upside} max={100} icon={TrendingUp} onChange={v => updateLever('salary_upside', v)} display={levers.salary_upside > 66 ? 'Optimistic' : levers.salary_upside > 33 ? 'Moderate' : 'Conservative'} hint="How strong salary growth will be in your field" />
                <Lever label="Market Competition" value={levers.market_comp} max={100} icon={Activity} onChange={v => updateLever('market_comp', v)} display={levers.market_comp > 66 ? 'Hot Market' : levers.market_comp > 33 ? 'Normal' : 'Tight Market'} hint="How competitive the job market is" />
                <Lever label="Risk Tolerance" value={levers.risk_tolerance} max={100} icon={Shield} onChange={v => updateLever('risk_tolerance', v)} display={levers.risk_tolerance > 66 ? 'Aggressive' : levers.risk_tolerance > 33 ? 'Balanced' : 'Conservative'} hint="Your comfort with higher-risk/higher-reward paths" />
                <Lever label="AI Disruption" value={levers.ai_disruption} max={100} icon={Zap} onChange={v => updateLever('ai_disruption', v)} display={levers.ai_disruption > 66 ? 'High Impact' : levers.ai_disruption > 33 ? 'Medium' : 'Low Impact'} hint="How much AI will change your target role" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* GRAD SCHOOL ROI + TIMELINE + BEST MOVE */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <motion.div variants={fadeUp}>
            <div className="relative overflow-hidden rounded-2xl border border-[rgba(120,47,225,0.28)] bg-[#061736] p-5 h-full"
              style={{ boxShadow: '0 0 6px rgba(120,47,225,0.35), 0 0 14px rgba(120,47,225,0.12)' }} data-testid="grad-roi">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg border border-[rgba(120,47,225,0.35)] bg-[rgba(120,47,225,0.08)] flex items-center justify-center">
                  <GraduationCap className="w-4 h-4 text-[#782FE1]" />
                </div>
                <h2 className="text-white font-outfit font-bold text-base">Grad School ROI</h2>
              </div>
              <div className="space-y-3">
                <div className="rounded-xl border border-[rgba(120,47,225,0.25)] bg-[rgba(120,47,225,0.06)] p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-[#782FE1]" />
                    <p className="text-sm text-white font-semibold">Stay for Master's</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-outfit font-black text-white">{grad_school_roi.masters.ten_yr_roi}</p>
                    <p className="text-[10px] text-slate-300">{grad_school_roi.masters.break_even}</p>
                  </div>
                </div>
                <div className="rounded-xl border border-[rgba(21,186,137,0.25)] bg-[rgba(21,186,137,0.06)] p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-[#15BA89]" />
                    <p className="text-sm text-white font-semibold">Enter Workforce Now</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-outfit font-black text-white">{grad_school_roi.workforce_now.ten_yr_roi}</p>
                    <p className="text-[10px] text-slate-300">{grad_school_roi.workforce_now.break_even}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeUp}>
            <div className="relative overflow-hidden rounded-2xl border border-[rgba(57,227,220,0.22)] bg-[#061736] p-5 h-full"
              style={{ boxShadow: '0 0 6px rgba(57,227,220,0.35), 0 0 14px rgba(57,227,220,0.12)' }} data-testid="sim-timeline">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg border border-[rgba(57,227,220,0.28)] bg-[rgba(57,227,220,0.06)] flex items-center justify-center">
                  <Clock className="w-4 h-4 text-[#39E3DC]" />
                </div>
                <h2 className="text-white font-outfit font-bold text-base">Action Timeline</h2>
              </div>
              <div className="space-y-3">
                {[
                  { key: 'now', label: 'NOW', color: '#39E3DC' },
                  { key: 'one_year', label: '1 YEAR', color: '#15BA89' },
                  { key: 'two_years', label: '2 YEARS', color: '#782FE1' },
                  { key: 'three_plus', label: '3+ YEARS', color: '#ECC451' },
                ].map((phase, i) => (
                  <div key={phase.key} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: phase.color, boxShadow: `0 0 8px ${phase.color}66` }} />
                      {i < 3 && <div className="w-px flex-1 bg-[rgba(89,126,190,0.15)] mt-1" />}
                    </div>
                    <div className="pb-2">
                      <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: phase.color }}>{phase.label}</p>
                      {timeline[phase.key]?.map((item, j) => (
                        <p key={j} className="text-[11px] text-[#C6D4E8] mb-0.5">• {item}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeUp}>
            <div className="relative overflow-hidden rounded-2xl p-5 h-full"
              style={{
                background: 'linear-gradient(145deg, rgba(19,22,58,0.92), rgba(27,13,64,0.82))',
                border: '1px solid rgba(120,47,225,0.55)',
                boxShadow: '0 0 8px rgba(120,47,225,0.55), 0 0 20px rgba(120,47,225,0.25)',
              }} data-testid="best-move">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-[#ECC451]" style={{ filter: 'drop-shadow(0 0 4px rgba(236,196,81,0.5))' }} />
                <h2 className="text-white font-outfit font-bold text-base">Best Next Move</h2>
              </div>
              <div className="rounded-xl border border-[rgba(236,196,81,0.25)] bg-[rgba(236,196,81,0.06)] p-4 mb-3"
                style={{ boxShadow: '0 0 14px -4px rgba(236,196,81,0.15)' }}>
                <p className="text-[10px] text-[#ECC451] uppercase tracking-wider font-bold mb-1">Recommended strategy</p>
                <p className="text-white text-base font-outfit font-bold">{best_move.best_label}</p>
                <p className="text-[#C6D4E8] text-xs mt-1">{paths[best_move.best]?.description}</p>
              </div>
              <div className="rounded-xl border border-[rgba(57,227,220,0.2)] bg-[rgba(57,227,220,0.04)] p-4">
                <p className="text-[10px] text-[#39E3DC] uppercase tracking-wider font-bold mb-1">Runner-up</p>
                <p className="text-white text-base font-outfit font-bold">{best_move.second_label}</p>
                <p className="text-[#C6D4E8] text-xs mt-1">{paths[best_move.second]?.description}</p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div variants={fadeUp}>
          <div className="rounded-2xl border border-[rgba(89,126,190,0.15)] bg-[#061736] p-5 text-center">
            <p className="text-xl font-outfit text-[#C6D4E8]">
              Not just what could happen. <span className="font-bold text-[#39E3DC]" style={{ textShadow: '0 0 20px rgba(57,227,220,0.4)' }}>The smartest play, right now.</span>
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AppLayout>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   LEVER SLIDER
   ═══════════════════════════════════════════════════════════════════════ */
function Lever({ label, value, max, unit, icon: Icon, onChange, display, hint }) {
  const pct = (value / max) * 100;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5 text-[#C6D4E8]" />
          <p className="text-xs text-white font-medium">{label}</p>
        </div>
        <span className="text-[11px] font-bold text-[#39E3DC]">{display || `${value}${unit ? ' ' + unit : ''}`}</span>
      </div>
      {hint && <p className="text-[10px] text-[#C6D4E8]/60 mb-1.5 ml-5">{hint}</p>}
      <div className="relative h-2 rounded-full bg-[rgba(89,126,190,0.15)] cursor-pointer" onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        onChange(Math.round(Math.max(0, Math.min(1, x)) * max));
      }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #352CA0, #782FE1, #2E86E2)', boxShadow: '0 0 8px rgba(120,47,225,0.35)' }} />
        <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white border-2"
          style={{ left: `calc(${pct}% - 7px)`, borderColor: '#39E3DC', boxShadow: '0 0 8px rgba(57,227,220,0.4)' }} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   SALARY CHART, Pure HTML/CSS chart (no SVG rendering issues)
   ═══════════════════════════════════════════════════════════════════════ */
function SalaryChart({ paths, combined, activePath }) {
  if (!paths?.length) return <p className="text-center text-slate-300 py-8">No path data available.</p>;

  const years = [0, 1, 3, 5, 10];
  const yearLabels = ['Now', 'Yr 1', 'Yr 3', 'Yr 5', 'Yr 10'];

  const allPaths = combined
    ? [...paths, { ...combined, id: 'combined', label: 'Combined', color: '#39E3DC' }]
    : paths;

  const allVals = allPaths.flatMap(p => years.map(y => p.projections[y] || 0));
  const minV = Math.max(0, Math.min(...allVals) * 0.85);
  const maxV = Math.max(...allVals) * 1.05;
  const range = maxV - minV || 1;
  const pct = (v) => ((v - minV) / range) * 100;
  const fmtK = (v) => `$${Math.round(v / 1000)}K`;

  return (
    <div>
      {/* Y-axis + chart area */}
      <div className="flex gap-0">
        {/* Y-axis labels */}
        <div className="flex flex-col justify-between w-12 flex-shrink-0 pr-2" style={{ height: 220 }}>
          {[100, 75, 50, 25, 0].map(p => (
            <span key={p} className="text-[9px] text-[#C6D4E8] text-right leading-none">{fmtK(minV + range * p / 100)}</span>
          ))}
        </div>

        {/* Plot area */}
        <div className="flex-1 relative" style={{ height: 220 }}>
          {/* Horizontal grid lines */}
          {[0, 25, 50, 75, 100].map(p => (
            <div key={p} className="absolute left-0 right-0 h-px" style={{ bottom: `${p}%`, background: 'rgba(89,126,190,0.1)' }} />
          ))}

          {/* Lines + dots for each path */}
          {allPaths.map((path) => {
            const isAct = path.id === activePath || path.id === 'combined';
            const isCombined = path.id === 'combined';
            const points = years.map((y, i) => ({
              x: (i / (years.length - 1)) * 100,
              y: pct(path.projections[y] || 0),
              val: path.projections[y] || 0,
            }));

            return (
              <div key={path.id} className="absolute inset-0" style={{ zIndex: isAct ? 10 : 1, opacity: isAct ? 1 : 0.3 }}>
                {/* SVG line with viewBox coordinate space */}
                <svg className="absolute inset-0 w-full h-full overflow-visible" viewBox="0 0 1000 1000" preserveAspectRatio="none">
                  <polyline
                    points={points.map(p => `${p.x * 10},${(100 - p.y) * 10}`).join(' ')}
                    fill="none"
                    stroke={path.color}
                    strokeWidth={isAct ? 25 : 10}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray={isCombined ? '40,20' : 'none'}
                    vectorEffect="non-scaling-stroke"
                  />
                  {isAct && (
                    <polyline
                      points={points.map(p => `${p.x * 10},${(100 - p.y) * 10}`).join(' ')}
                      fill="none"
                      stroke={path.color}
                      strokeWidth={60}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity="0.15"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
                </svg>

                {/* Dots */}
                {points.map((p, i) => (
                  <div key={i} className="absolute" style={{
                    left: `${p.x}%`, bottom: `${p.y}%`,
                    transform: 'translate(-50%, 50%)',
                    zIndex: 5,
                  }}>
                    <div className="rounded-full border-2 border-[#030C1D]" style={{
                      width: isAct ? 10 : 6, height: isAct ? 10 : 6,
                      backgroundColor: path.color,
                      boxShadow: isAct ? `0 0 6px ${path.color}88` : 'none',
                    }} />
                  </div>
                ))}

                {/* End label */}
                {isAct && (
                  <div className="absolute" style={{
                    left: `calc(100% + 6px)`,
                    bottom: `${pct(path.projections[10] || 0)}%`,
                    transform: 'translateY(50%)',
                    whiteSpace: 'nowrap',
                  }}>
                    <span className="text-[11px] font-bold" style={{ color: path.color }}>{fmtK(path.projections[10] || 0)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right margin for end labels */}
        <div className="w-14 flex-shrink-0" />
      </div>

      {/* X-axis */}
      <div className="flex ml-12 mr-14 mt-1">
        {yearLabels.map((l, i) => (
          <span key={i} className="text-[10px] text-[#C6D4E8] text-center" style={{ width: `${100 / yearLabels.length}%` }}>{l}</span>
        ))}
      </div>
    </div>
  );
}
